// src/components/DeliveryList.js
import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import {
  getOrdersByDateAndCarrier,
  getCarriers,
  logError,
  getBulkAlbaranes,
  getCombinedAlbaranesPdf
} from '../api';
import { handlePrintSummary } from './Global/DownloadUtils';
import {
  getCarrierId,
  isTransportistaAndNotOwnerAdminGestion,
  isJefeTransportistas
} from '../services/AuthService';
import { formatDateToYYYYMMDD } from '../utils/DateUtils';
import DeliveryForm from './DeliveryForm';
import Pagination from './Global/Pagination';
import TotalCount from './Global/TotalCount';
import useFilterPersistence from '../hooks/useFilterPersistence';
import "react-datepicker/dist/react-datepicker.css";
// import './Global/Style/DeliveryList.css'; // Comentado para usar el nuevo diseño
import { Button } from 'react-bootstrap';

const DeliveryList = ({ carriers }) => {
  const isTransportista = isTransportistaAndNotOwnerAdminGestion();

  const [deliveryState, setDeliveryState] = useFilterPersistence('filters_delivery', {
    selectedDate: formatDateToYYYYMMDD(new Date()),
    carrierId: isTransportista ? getCarrierId() : '-1',
    zipCodeFilter: '',
    page: 1,
    pageSize: undefined
  });
  const { selectedDate: selectedDateStr, carrierId, zipCodeFilter, page: currentPage, pageSize: ordersPerPage } = deliveryState;
  const selectedDate = selectedDateStr ? new Date(selectedDateStr + 'T00:00:00') : new Date();
  const setSelectedDate = (date) => setDeliveryState({ ...deliveryState, selectedDate: formatDateToYYYYMMDD(date), page: 1 });
  const setCarrierId = (val) => setDeliveryState({ ...deliveryState, carrierId: val, page: 1 });
  const setZipCodeFilter = (val) => setDeliveryState({ ...deliveryState, zipCodeFilter: val });

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [printingAll, setPrintingAll] = useState(false);
  const [printingResumen, setPrintingResumen] = useState(false);
  const [allCarriers, setAllCarriers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [debouncedZipCodeFilter, setDebouncedZipCodeFilter] = useState(zipCodeFilter);

  useEffect(() => {
    if (!isTransportista || isJefeTransportistas()) {
      getCarriers()
        .then(setAllCarriers)
        .catch(error => {
          logError(error);
          console.error('Error al obtener los carriers:', error);
        });
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedZipCodeFilter(zipCodeFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [zipCodeFilter]);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!selectedDateStr || !carrierId) return;
      setLoading(true);
      try {
        const zipCodes = debouncedZipCodeFilter ? debouncedZipCodeFilter.split(',').map(code => code.trim()).filter(code => code) : null;
        const fetched = await getOrdersByDateAndCarrier(
          selectedDateStr,
          carrierId,
          currentPage,
          ordersPerPage,
          zipCodes
        );
        if (Array.isArray(fetched.items)) {
          setOrders(fetched.items);
          setTotalCount(fetched.totalCount);
        } else {
          setOrders([]);
          setTotalCount(0);
        }
      } catch (error) {
        logError(error);
        console.error('Error al obtener los pedidos:', error);
        setOrders([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [selectedDateStr, carrierId, currentPage, ordersPerPage, debouncedZipCodeFilter]);

  const fetchAllSelectedOrders = async () => {
    if (!selectedDateStr || !carrierId) return;
    setLoading(true);
    try {
      const zipCodes = debouncedZipCodeFilter ? debouncedZipCodeFilter.split(',').map(code => code.trim()).filter(code => code) : null;
      return await getOrdersByDateAndCarrier(
        selectedDateStr,
        carrierId,
        1,
        totalCount,
        zipCodes
      );
    } catch (error) {
      logError(error);
      console.error('Error al obtener todos los pedidos:', error);
      return { items: [] };
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAlbaranesZip = async () => {
    try {
      const all = await fetchAllSelectedOrders();
      const ids = all.items.map(o => o.id);
      const blob = await getBulkAlbaranes(ids);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Albaranes.zip';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      logError(error);
      alert('Error al descargar los albaranes.');
    }
  };

  const handlePrintCombinedAlbaranes = async () => {
    setPrintingAll(true);
    try {
      const all = await fetchAllSelectedOrders();
      const ids  = all.items.map(o => o.id);
      const response = await getCombinedAlbaranesPdf(ids);

      if (response?.data?.base64Pdf) {
        const base64String = response.data.base64Pdf;

        // Convertir base64 a Blob
        const byteCharacters = atob(base64String);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        // Crear URL de Blob y abrir ventana para imprimir
        const blobUrl = URL.createObjectURL(blob);
        const printWindow = window.open(blobUrl);
        printWindow.onload = () => {
          printWindow.focus();
          printWindow.print();
          URL.revokeObjectURL(blobUrl);
        };
      } else {
        alert('El archivo PDF combinado está vacío o no se pudo obtener.');
      }
    } catch (error) {
      logError(error);
      alert('Error al descargar o imprimir los albaranes combinados.');
    } finally {
      setPrintingAll(false);
    }
  };

  return (
    <div className="container-fluid px-3 py-4" style={{ maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
            <h1 className="h3 mb-2 mb-md-0 text-primary" data-testid="delivery-page-title">🚚 Entregas</h1>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex flex-column flex-md-row gap-4 align-items-md-center">
                <div className="d-flex align-items-center gap-3">
                  <label className="form-label small text-muted mb-0 text-nowrap">📅 Fecha de entrega:</label>
                  <div data-testid="delivery-date-picker">
                    <DatePicker
                      selected={selectedDate}
                      onChange={date => setSelectedDate(date)}
                      className="form-control"
                      style={{ maxWidth: '200px' }}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="Seleccionar fecha"
                    />
                  </div>
                </div>
                
                {(!isTransportista || isJefeTransportistas()) && allCarriers.length > 0 && (
                  <div className="d-flex align-items-center gap-3">
                    <label className="form-label small text-muted mb-0 text-nowrap">🚚 Transportista:</label>
                    <select
                      className="form-select"
                      style={{ maxWidth: '300px' }}
                      value={carrierId}
                      onChange={e => setCarrierId(e.target.value)}
                      data-testid="carrier-select"
                    >
                      <option value="-1">Todos los transportistas</option>
                      {allCarriers.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.firstName} {c.lastName1}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div className="d-flex align-items-center gap-3">
                  <label className="form-label small text-muted mb-0 text-nowrap">📮 Códigos Postales:</label>
                  <input
                    type="text"
                    className="form-control"
                    style={{ maxWidth: '250px' }}
                    value={zipCodeFilter}
                    onChange={e => setZipCodeFilter(e.target.value)}
                    placeholder="Ej: 28001, 28002, 28003"
                    data-testid="zipcode-filter"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      {orders.length > 0 && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex flex-column flex-md-row gap-2">
                  <Button
                    variant="outline-primary"
                    onClick={handleDownloadAlbaranesZip}
                    className="d-flex align-items-center justify-content-center gap-2"
                    data-testid="download-zip-btn"
                  >
                    📦 Descargar ZIP
                  </Button>
                  <Button
                    variant="outline-success"
                    onClick={handlePrintCombinedAlbaranes}
                    disabled={printingAll}
                    className="d-flex align-items-center justify-content-center gap-2"
                    data-testid="print-all-btn"
                  >
                    {printingAll ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Imprimiendo...
                      </>
                    ) : (
                      '🖨️ Imprimir Todo'
                    )}
                  </Button>
                  <Button
                    variant="outline-info"
                    onClick={async () => {
                      setPrintingResumen(true);
                      try {
                        const all = await fetchAllSelectedOrders();
                        const orderIds = all.items.map(o => o.id);
                        await handlePrintSummary(orderIds);
                      } finally {
                        setPrintingResumen(false);
                      }
                    }}
                    disabled={printingResumen}
                    className="d-flex align-items-center justify-content-center gap-2"
                    data-testid="print-summary-btn"
                  >
                    {printingResumen ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Imprimiendo...
                      </>
                    ) : (
                      '🖨️ Imprimir Resumen'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Orders List */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="card-title text-muted mb-0">📋 Lista de Pedidos</h6>
                {!loading && <TotalCount totalCount={totalCount} />}
                {selectedDate && (
                  <span className="small text-muted">
                    {selectedDate.toLocaleDateString('es-ES', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </span>
                )}
              </div>
              
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary mb-3" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                  <div className="text-muted">Cargando pedidos...</div>
                </div>
              ) : orders.length > 0 ? (
                <div className="row g-3">
                  {orders.map(order => (
                    <div key={order.id} className="col-12">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body p-0">
                          <DeliveryForm order={order} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-5">
                  <div className="mb-3" style={{ fontSize: '3rem' }}>📦</div>
                  <h5 className="text-muted mb-2">No hay pedidos</h5>
                  <p className="text-muted mb-0">
                    No se encontraron pedidos para entregar en la fecha seleccionada
                  </p>
                  {selectedDate && (
                    <p className="small text-muted mt-2">
                      Fecha: {selectedDate.toLocaleDateString('es-ES')}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {totalCount > 0 && (
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-center">
              <Pagination
                totalCount={totalCount}
                currentPage={currentPage}
                itemsPerPage={ordersPerPage}
                onPageChange={page => setDeliveryState({ ...deliveryState, page })}
                onItemsPerPageChange={perPage => setDeliveryState({ ...deliveryState, page: 1, pageSize: perPage })}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryList;
